import { toast } from 'sonner';
import { registerActionHandler } from '../action-registry';
import { buildSmallAppUrl } from '../smallapp-url';
import { getSmallAppRestToken } from '../get-smallapp-rest-token';

/**
 * `refreshDataSource` — Refresh Data action for the Google Sheets / Microsoft Excel
 * data-source manifests.
 *
 * POSTs `datasources/refresh` with ONLY `{ assetId }` — NOT the OAuth profile. The backend
 * scrubs `access_token` / `refresh_token` from `Asset.profile` on every GraphQL read
 * (`scrubProfileSecrets`), so the scio3 form never holds tokens. `/refresh` resolves the
 * asset by id, renews the token + reloads the sheet list server-side, and **persists
 * `profile` + `sheetList` + `sheetSelected` back to the asset itself** — so there is
 * nothing to merge on the FE. It returns only `{ status: true }` (or
 * `{ status: false, message }` on failure); we just surface a toast.
 *
 * Uses the same Firebase/share-link REST token as `useFetchOptions`
 * (`getSmallAppRestToken`) — the open-api proxy's service token 403s here.
 *
 * Disabled in add-mode (`assetId` only exists after the first save).
 *
 * Manifest usage:
 *   { key: 'sheetSelected', type: 'select', action: {
 *       label: 'Refresh Data', handler: 'refreshDataSource', disabled: 'add',
 *       onError: { message: 'Something went wrong! Please try again.' } } }
 */
registerActionHandler('refreshDataSource', async ({ values, appMeta, apolloClient, t }) => {
  const apiUrl = appMeta?.apiUrl;
  const assetId = values.assetId;
  if (!apiUrl || !assetId) {
    toast.error(t('This app does not expose a data-source API'));
    return;
  }

  const url = buildSmallAppUrl(apiUrl, 'datasources/refresh');
  // Same token method as useFetchOptions — Firebase/share-link-aware REST token the
  // data-source microservice accepts (the open-api proxy's service token 403s).
  const token = await getSmallAppRestToken(apolloClient);

  const response = await fetch(url, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
    body: JSON.stringify({
      // Send only the id; the backend resolves the asset (tokens + sheet list) from the DB
      // and persists the refreshed profile/sheetList/sheetSelected server-side.
      assetId: String(assetId),
    }),
  });

  if (!response.ok) {
    toast.error(t('Failed to refresh data'));
    return;
  }

  const data = (await response.json()) as Record<string, unknown>;
  // /refresh returns { status:true } on success; { status:false, message } on failure.
  if (!data || data.status === false) {
    const message = typeof data?.message === 'string' ? data.message : 'Failed to refresh data';
    toast.error(t(message));
    return;
  }

  toast.success(t('Data refreshed'));
});
